<template>
  <div class="learner-link-item">
    <a
      class="learner-link-main"
      :href="link.url"
      target="_blank"
      rel="noopener noreferrer"
      :aria-label="linkAriaLabel"
      :aria-describedby="showUrlOnTile ? linkUrlId : undefined"
    >
      <div
        class="learner-link-icon"
        :style="{ backgroundColor: iconBackground }"
        aria-hidden="true"
      >
        <i :class="linkIconClasses" aria-hidden="true"></i>
      </div>
      <div class="learner-link-details">
        <span class="learner-link-label">
          <span class="learner-link-label-text">{{ displayLabel }}</span>
          <i
            class="fas fa-external-link-alt learner-link-external-icon"
            aria-hidden="true"
          ></i>
        </span>
        <span
          v-if="showUrlOnTile"
          :id="linkUrlId"
          class="learner-link-url"
        >{{ link.url }}</span>
      </div>
    </a>
    <div v-if="canManage" class="learner-link-item-controls">
      <span
        v-if="showShareIndicator"
        class="learner-link-share-indicator"
      >
        <k-tooltip
          text="Shared on learner homepage"
          trigger-label="Shared on learner homepage"
          position="right"
        >
          <i class="fas fa-eye" aria-hidden="true"></i>
        </k-tooltip>
      </span>
      <button
        type="button"
        class="btn btn-edit"
        aria-label="Edit link"
        @click.stop="$emit('edit', link)"
      >
        <i class="fa-duotone fa-pencil" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="btn btn-trash"
        aria-label="Delete link"
        @click.stop="$emit('delete', link)"
      >
        <i class="fa-duotone fa-trash-can" aria-hidden="true"></i>
      </button>
    </div>
  </div>
</template>

<style scoped>
.learner-link-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--kate-note-alt);
  border-radius: 4px;
  box-shadow: var(--box-shadow);
  transition: background-color 0.15s ease;
}

.learner-link-item:hover,
.learner-link-item:focus-within {
  background-color: var(--kate-background-alt-alpha);
}

.learner-link-main {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}

.learner-link-main:focus-visible {
  outline: 2px solid var(--kate-type-primary);
  outline-offset: 2px;
}

.learner-link-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  min-width: 40px;
  height: 40px;
  border-radius: 4px;
  color: var(--kate-type-dark);
}

.learner-link-details {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.learner-link-label {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-width: 0;
}

.learner-link-label-text,
.learner-link-url {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.learner-link-label-text {
  min-width: 0;
}

.learner-link-external-icon {
  --learner-link-external-icon-offset: 5px;

  flex-shrink: 0;
  margin-left: calc(0.35em + var(--learner-link-external-icon-offset));
  font-size: 0.85em;
  color: var(--kate-mono-3);
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.learner-link-item:hover .learner-link-external-icon,
.learner-link-item:focus-within .learner-link-external-icon {
  opacity: 1;
  transform: translateX(0);
}

@media (hover: none) {
  .learner-link-external-icon {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .learner-link-external-icon {
    transition: none;
  }
}

.learner-link-url {
  font-size: 0.85em;
  color: var(--kate-mono-3);
}

.learner-link-item-controls {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.learner-link-item:hover .learner-link-item-controls,
.learner-link-item:focus-within .learner-link-item-controls {
  opacity: 1;
}

@media (hover: none) {
  .learner-link-item-controls {
    opacity: 1;
  }
}

.learner-link-share-indicator {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  color: var(--kate-mono-3);
  font-size: 0.95rem;
  line-height: 1;
}

.learner-link-item-controls button i {
  text-shadow: 2px 2px 0 var(--kate-type-dark);
}

.learner-link-item-controls .btn-trash i {
  color: var(--kate-danger-alt);
}

.learner-link-item-controls .btn-trash:hover i {
  color: var(--kate-danger);
}
</style>

<script>
import { KTooltip } from '@kate/components';
import {
  getLearnerLinkIconClasses,
  learnerLinkColorCssVars,
} from '@utils/learner-link-presets';

export default {
  components: {
    KTooltip,
  },

  props: {
    link: {
      type: Object,
      required: true,
    },
    canManage: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['edit', 'delete'],

  computed: {
    hasCustomLabel() {
      return Boolean(this.link.label?.trim());
    },

    showUrlOnTile() {
      return this.canManage && this.hasCustomLabel;
    },

    displayLabel() {
      return this.hasCustomLabel ? this.link.label : 'External Link';
    },

    showShareIndicator() {
      return this.canManage && this.link.share_with_learner;
    },

    linkAriaLabel() {
      let base;
      if (this.hasCustomLabel) {
        base = `Open ${this.link.label}`;
      } else if (this.canManage) {
        base = `Open external link, ${this.link.url}`;
      } else {
        base = 'Open external link';
      }
      const shareSuffix = this.showShareIndicator ? ' Shared on learner homepage.' : '';
      return `${base} (opens in new tab).${shareSuffix}`;
    },

    linkUrlId() {
      return `learner-link-url-${this.link.id}`;
    },

    iconBackground() {
      const cssVar = learnerLinkColorCssVars[this.link.icon_color];
      return cssVar ? `var(${cssVar})` : `var(${learnerLinkColorCssVars['brand-blue']})`;
    },

    linkIconClasses() {
      return getLearnerLinkIconClasses(this.link.icon);
    },
  },
};
</script>
